'use client';

import { Search, X } from 'lucide-react';
import { FC, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle
} from '@components/ui/drawer';
import { cn } from '@utils/cn';
import type {
  DefaultSaveStatus,
  KnowledgeSearchStatus
} from '@hooks/use-knowledge-search';
import type { SearchHit, SearchResponseMode } from '../../search/types';
import KnowledgeSearchAnswerCard from './KnowledgeSearchAnswerCard';
import KnowledgeSearchDefaultControls from './KnowledgeSearchDefaultControls';
import type { KnowledgeSearchRowData } from './KnowledgeSearchRow';
import KnowledgeSearchSkeletonRow from './KnowledgeSearchSkeletonRow';

type KnowledgeSearchMobileProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationName: string | null | undefined;
  term: string;
  onTermChange: (term: string) => void;
  onSubmit: () => void;
  rows: KnowledgeSearchRowData[];
  emptyMessage: string | null;
  loadingLabel: string;
  defaultLabel: string | null;
  status: KnowledgeSearchStatus;
  onRetry: () => void;
  mode: SearchResponseMode | null;
  saveStatus: DefaultSaveStatus;
  onSaveAsDefault: () => void;
  onResetDefault: () => void;
  answer: { text: string; citedHitIds: string[] } | null;
  results: SearchHit[];
  onCitationClick: (hitId: string) => void;
  onAskAboutThis?: () => void;
};

/**
 * Full-screen mobile knowledge search. Same Outcome hierarchy as the
 * desktop dialog (eyebrow, heading, large field, rows), adapted to a
 * full-width small screen with the header/input pinned while results
 * scroll -- matches the previous SearchDrawer's chrome (close circle, pill
 * field) so the surrounding shell doesn't feel like two different apps.
 */
const KnowledgeSearchMobile: FC<KnowledgeSearchMobileProps> = ({
  open,
  onOpenChange,
  organizationName,
  term,
  onTermChange,
  onSubmit,
  rows,
  emptyMessage,
  loadingLabel,
  defaultLabel,
  status,
  onRetry,
  mode,
  saveStatus,
  onSaveAsDefault,
  onResetDefault,
  answer,
  results,
  onCitationClick,
  onAskAboutThis
}) => {
  const t = useTranslations('knowledge-search');
  const common = useTranslations('common');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
    >
      <DrawerContent
        hideHandle
        aria-describedby={undefined}
        className="inset-0 mt-0 flex h-dvh max-h-dvh flex-col rounded-none border-0"
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <div className="min-w-0">
            {organizationName ? (
              <div className="truncate text-column uppercase tracking-wider text-muted-foreground">
                {organizationName}
              </div>
            ) : null}
            <DrawerTitle className="text-card-title">
              {t('heading')}
            </DrawerTitle>
          </div>
          <DrawerClose
            aria-label={common('close')}
            className="chrome-glass flex size-11 shrink-0 items-center justify-center rounded-full"
          >
            <X className="size-5" />
          </DrawerClose>
        </div>

        <div className="px-4 pb-3">
          <div className="flex h-12 items-center gap-2 rounded-full bg-muted px-4">
            <Search
              className="size-5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              type="search"
              inputMode="search"
              value={term}
              onChange={event => onTermChange(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') onSubmit();
              }}
              placeholder={t('placeholder')}
              className="h-full flex-1 bg-transparent text-body outline-none placeholder:text-muted-foreground"
              aria-label={t('placeholder')}
            />
            {term ? (
              <button
                type="button"
                onClick={() => onTermChange('')}
                aria-label={t('placeholder')}
                className="flex size-8 items-center justify-center text-muted-foreground"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
          {defaultLabel ? (
            <div className="mt-2 flex items-center justify-between px-1">
              <span className="text-caption text-muted-foreground">
                {defaultLabel}
              </span>
              <KnowledgeSearchDefaultControls
                mode={mode}
                status={status}
                termLength={term.trim().length}
                saveStatus={saveStatus}
                onSave={onSaveAsDefault}
                onReset={onResetDefault}
              />
            </div>
          ) : (
            <div className="mt-1 px-1">
              <KnowledgeSearchDefaultControls
                mode={mode}
                status={status}
                termLength={term.trim().length}
                saveStatus={saveStatus}
                onSave={onSaveAsDefault}
                onReset={onResetDefault}
              />
            </div>
          )}
        </div>

        {answer ? (
          <KnowledgeSearchAnswerCard
            answerText={answer.text}
            citedHitIds={answer.citedHitIds}
            results={results}
            onCitationClick={onCitationClick}
            onAskAboutThis={onAskAboutThis}
          />
        ) : null}

        <div className="flex-1 overflow-y-auto" aria-live="polite">
          {status === 'loading' ? (
            <>
              <p className="px-4 pb-2 pt-1 text-caption text-muted-foreground">
                {loadingLabel}
              </p>
              <KnowledgeSearchSkeletonRow />
              <KnowledgeSearchSkeletonRow />
              <KnowledgeSearchSkeletonRow />
              <KnowledgeSearchSkeletonRow />
            </>
          ) : emptyMessage ? (
            status === 'error' ? (
              <div
                role="alert"
                className="flex flex-col items-center gap-3 px-6 py-12 text-center"
              >
                <p className="text-body text-muted-foreground">
                  {emptyMessage}
                </p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="min-h-11 rounded-md border border-border px-4 text-label"
                >
                  {t('retry')}
                </button>
              </div>
            ) : (
              <p className="px-6 py-12 text-center text-body text-muted-foreground">
                {emptyMessage}
              </p>
            )
          ) : (
            <ul>
              {rows.map(row => (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={row.onSelect}
                    data-hit-id={row.hitId}
                    className={cn(
                      'flex w-full items-center gap-4 border-b border-border px-4 py-4 text-left transition-colors',
                      row.highlighted && 'bg-accent'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-column uppercase tracking-wider text-muted-foreground">
                        {row.typeLabel}
                      </div>
                      <div className="mt-1 truncate text-card-title text-foreground">
                        {row.title}
                      </div>
                      {row.subtitle ? (
                        <div className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">
                          {row.subtitle}
                        </div>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default KnowledgeSearchMobile;
